import type { MealPatternSlotName } from '@steado/admin-contract/meal-patterns';
import { useQuery } from '@tanstack/react-query';

import { Panel } from '@/components/panel/panels';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { humanise } from '@/lib/format';

import { previewSlot } from './meal-patterns-query';

/**
 * What this slot's live grammar would reject, measured against real meals.
 *
 * Not a simulation: it walks committed meals through the generator's own
 * matching, so the number is what the rules have actually been doing rather
 * than what they were meant to do. The slot's shapes are checked together,
 * because a meal has to satisfy all of them at once — which is why this cannot
 * be a per-version preview, and why a seeded shape (which has no row and so no
 * id) is included here and nowhere else.
 */
export function SlotPreviewPanel({ slot }: { readonly slot: MealPatternSlotName }) {
  const { api } = useAuth();
  const query = useQuery({
    queryKey: ['admin', 'meal-pattern-slot-preview', slot],
    queryFn: () => previewSlot(api, slot),
  });

  const preview = query.data;

  return (
    <Panel
      title={`What turning this slot’s shapes on would mean`}
      description={`Real ${humanise(slot)} plates from recent committed weeks, run through the generator’s own matching.`}
      bleed
    >
      {query.isError ? (
        <p role="alert" className="px-[18px] py-6 text-[13px] text-err-fg">
          {query.error.message}
        </p>
      ) : preview === undefined ? (
        <p className="px-[18px] py-6 text-[13px] text-muted-foreground">Counting…</p>
      ) : (
        <>
          {/*
            Rows rather than a strip of cells: these four are one measurement
            read top to bottom, and "would be rejected" is the one the eye
            should stop on.
          */}
          <dl>
            {[
              { label: 'Plates checked', value: preview.mealsInSlot, tone: 'text-foreground' },
              { label: 'Would pass', value: preview.matched, tone: 'text-foreground' },
              {
                label: 'Would be rejected',
                value: preview.wouldReject,
                tone: preview.wouldReject === 0 ? 'text-ok-fg' : 'text-warn-fg',
              },
              { label: 'Weeks behind it', value: preview.weeks, tone: 'text-foreground' },
            ].map((row) => (
              <div
                key={row.label}
                className="flex items-baseline gap-3 border-t border-border px-[18px] py-2.5"
              >
                <dt className="text-[13px] text-muted-foreground">{row.label}</dt>
                <dd className={cn('ml-auto font-mono text-[13px] font-medium', row.tone)}>
                  {row.value.toLocaleString('en-IN')}
                </dd>
              </div>
            ))}
          </dl>

          {preview.samples.length === 0 ? null : (
            <div className="flex flex-col gap-1.5 border-t border-border bg-secondary px-[18px] pt-3 pb-3.5">
              <span className="text-xs font-semibold tracking-[0.055em] text-muted-foreground uppercase">
                Real plates it has no room for
              </span>
              <ul className="flex flex-col gap-1">
                {preview.samples.map((sample) => (
                  <li
                    key={`${sample.weekStartDate}-${sample.dayOfWeek}`}
                    className="flex flex-wrap items-baseline gap-2 text-[13px]"
                  >
                    <span className="font-mono text-xs text-muted-foreground">
                      {sample.weekStartDate} {sample.dayOfWeek}
                    </span>
                    <span className="text-foreground">
                      {sample.roles.map((role) => role.replace(/_/g, ' ')).join(' + ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
